import {
  useContext,
  MutableRefObject,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useParams, useNavigate  } from 'react-router-dom';
import { Link } from '@chakra-ui/react';
import { Box, useDisclosure } from '@chakra-ui/react';
import { PaymentElement, PaymentElementProps } from '@cloudbeds/payment-sdk';

import { AppConfig } from 'app.config';

import * as Html from 'view-layer/styled/html';
import { TocModal } from 'view-layer/components/common';
import { CheckboxControl } from 'view-layer/components/forms/controls/CheckboxControl';

import {
  FormData,
  GenericKeyValue,
  BillingData,
} from 'business-layer/types';
import { L10nContext } from 'business-layer/contexts/L10nContext';
import { GlobalContext } from 'business-layer/contexts/GlobalContext';
import { tocField } from 'business-layer/helpers/validation-rules';
import { eventbus, Events } from 'business-layer/helpers/eventbus';
import { splitHash } from 'business-layer/helpers/splitHash';
import { sprintf } from 'business-layer/helpers/sprintf';
import { useForm } from 'business-layer/hooks/useForm';

import { processPayment, processPaymentV2 } from 'data-layer/queries/processPayment';

import { PayByLinkTheme } from 'payment-element-themes/PayByLinkTheme';

interface FormProps {
  billing: BillingData[],
  payment: Partial<{
    id: string;
    reservationId: number;
    reservationIdentifier: number;
    status: string;
    country: string;
    captureMethod: string;
    amount: number;
    currency: string;
    cardType: string;
    cardNumber: string;
    paidDate: string;
    remains: number;
    firstName: string;
    lastName: string;
    cardholderName: string;
    email: string;
  }>,
}

export const LangToLocale: Record<string, string> = {
  ach: 'ach_UG',
  ca: 'ca_ES',
  de: 'de_DE',
  en: 'en_US',
  es: 'es_MX',
  fr: 'fr_FR',
  id: 'id_ID',
  it: 'it_IT',
  pt: 'pt_BR',
  th: 'th_TH',
}

const prepareSubmitData = (formData: FormData, paymentData: any) => {
  const billing = { ...paymentData.billingDetails };
  const {token: cardToken, cvc: cvv, ...rest } = paymentData;
  return {
    cardToken,
    ...formData,
    ...rest,
    ...billing,
    cvv,
  }
}

const prepareOxxoSubmitData = (paymentData: {
  name: string,
  email: string,
  paymentMethod: 'oxxo',
}) => {
  return {
    payment_method: {
      type: 'oxxo',
      billing_details: {
        name: paymentData.name,
        email: paymentData.email,
      }
    }
  }
}

const prepareAffirmSubmitData = (paymentData: {
  name: string,
  email: string,
  paymentMethod: 'affirm',
}) => {
  return {
    payment_method: {
      type: 'affirm',
    }
  }
}

const prepareExpressCheckoutSubmitData = (paymentData: {
  paymentMethodId: string,
  paymentMethod: 'stripe_payment_method',
}) => {
  return {
    payment_method: {
      type: 'stripe_payment_method',
      stripe_payment_method: {
        token: paymentData.paymentMethodId,
      }
    }
  }
}

export const Form = (props: any) => {
  const { payment }: FormProps = props;
  const { apiKey, propertyId } = props;

  const navigate = useNavigate();

  const { hash: payByLinkHash = '' } = useParams();
  const { onOpen, isOpen, onClose } = useDisclosure();

  const { labels, lang }: { labels: GenericKeyValue, lang: string } = useContext(L10nContext) as any;
  const { setProgress } = useContext(GlobalContext);

  const formRef = useRef<HTMLFormElement>(null);
  const paymentElementRef = useRef<any>();

  const { data: formData, handleSubmit, handleReset, handleChange, errors } = useForm<FormData>({
    validations: {
      toc: { ...tocField },
    },
  });

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isFormValid = await handleSubmit(e);
    const isGatewayFormValid = await paymentElementRef.current.validate();

    if (isFormValid && isGatewayFormValid) {
      setProgress(true);
      await paymentElementRef.current.submit();
      setProgress(false);
    }
  };

  const postSubmitForOxxo = useCallback(async (data: any) => {
    setProgress(true);
    const { payByLinkId } = splitHash(payByLinkHash);
    const oxxoPayload = { ...prepareOxxoSubmitData(data), payByLinkId };

    const { nextAction, statusMessage, status } = await processPaymentV2(oxxoPayload);
    if (status==='pending' && nextAction?.details?.url) {
      window.location.replace(nextAction.details.url);
    } else {
      const message = statusMessage || 'We are sorry, but we can\'t fulfill your request at the moment.';
      navigate('/error', { state: { message, payByLinkHash }});
    }

    setProgress(false);
  }, [payByLinkHash, setProgress, navigate]);

  const postSubmitForAffirm = useCallback(async (data: any) => {
    setProgress(true);
    const { payByLinkId } = splitHash(payByLinkHash);
    const affirmPayload = { ...prepareAffirmSubmitData(data), payByLinkId };

    const { nextAction, statusMessage, status } = await processPaymentV2(affirmPayload);
    if (status==='pending' && nextAction?.details?.url) {
      window.location.replace(nextAction.details.url);
    } else {
      const message = statusMessage || 'We are sorry, but we can\'t fulfill your request at the moment.';
      navigate('/error', { state: { message, payByLinkHash }});
    }

    setProgress(false);
  }, [payByLinkHash, setProgress, navigate]);


  const postSubmitForExpressCheckout = useCallback(async (data: any) => {
    setProgress(true);
    const { payByLinkId } = splitHash(payByLinkHash);
    const payload = { ...prepareExpressCheckoutSubmitData(data), payByLinkId };

    const { nextAction, statusMessage /*, paymentHash */ } = await processPaymentV2(payload);

    if (nextAction?.details?.client_secret) {
      // const redirectUrl = `${window.location.href}/processing/${paymentHash}`;
      const redirectUrl = nextAction.details.url;
      return {
        secret: nextAction.details.client_secret,
        redirect: redirectUrl,
      }
    } else {
      const message = statusMessage || 'We are sorry, but we can\'t fulfill your request at the moment.';
      navigate('/error', { state: { message, payByLinkHash }});
    }

    setProgress(false);
    return null;
  }, [navigate, payByLinkHash, setProgress]);


  const postSubmit = useCallback(async (data: any) => {
    setProgress(true);

    const { payByLinkId } = splitHash(payByLinkHash);
    const { paymentHash, redirectUrl, statusMessage } = await processPayment({ ...prepareSubmitData(formData, data), payByLinkId });

    setProgress(false);

    if (redirectUrl && redirectUrl?.url && redirectUrl?.method) {
      if (redirectUrl.method === 'GET') {
        window.location.replace(redirectUrl.url);
      }
      if (redirectUrl.method === 'POST' && redirectUrl.postData) {
        const redirectForm = document.createElement('form');
        redirectForm.setAttribute('action', redirectUrl.url);
        redirectForm.setAttribute('method', 'post');
        redirectForm.setAttribute('hidden', 'true');

        Object.keys(redirectUrl.postData).forEach((el: any) => {
          const input = document.createElement('input');
          input.setAttribute('type', 'hidden');
          input.setAttribute('name', el);
          input.setAttribute('value', redirectUrl.postData![el]);
          redirectForm.appendChild(input);
        })

        document.body.appendChild(redirectForm);
        redirectForm.submit();
      }
    }

    if (paymentHash) {
      const url = sprintf(AppConfig.paymentReturnUrl!, [payByLinkHash, paymentHash]);
      navigate(url);
    } else {
      const message = statusMessage || 'We are sorry, but we can\'t fulfill your request at the moment.';
      navigate('/error', { state: { message, payByLinkHash }});
    }
  }, [formData, navigate, payByLinkHash, setProgress]);

  const onReset = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleReset(e);
  };

  const onModalOpen = (event: any) => {
    event.stopPropagation();
    event.preventDefault();
    onOpen();
  };

  useEffect(() => {
    eventbus.on(Events.FormSubmit, () => {
      formRef.current?.dispatchEvent(
        new Event("submit", {
          bubbles: true,
          cancelable: true
        })
      );
    });
    eventbus.on(Events.FormReset, () => {
      formRef.current?.dispatchEvent(
        new Event("reset", {
          bubbles: true,
          cancelable: true
        })
      );
    });
    return () => {
      eventbus.off(Events.FormSubmit);
      eventbus.off(Events.FormReset);
    }
  });

  const defaultProps: PaymentElementProps = useMemo(() => ({
    propertyId,
    apiKey,
    lang: LangToLocale[lang] || 'en_US',
    theme: PayByLinkTheme,
    ...(payment.amount && { amount: payment.amount }),
    ...(payment.currency && { currency: (payment.currency || 'usd').toLowerCase() }),
    guest: {
      firstName: payment.firstName || '',
      lastName: payment.lastName || '',
      email: payment.email,
      billingDetails: {
        billingAddress: '',
        billingState: '',
        billingCity: '',
        billingZip: '',
        billingCountry: '',
        billingEmail: '',
      },
    },
    onSubmit: async (data: any) => {
      if (data.paymentMethod === 'oxxo') {
        return postSubmitForOxxo(data);
      } else if (data.paymentMethod === 'affirm') {
        return postSubmitForAffirm(data);
      } else if (data.paymentMethod === 'apple_pay' || data.paymentMethod === 'google_pay') {
        return postSubmitForExpressCheckout(data);
      } else {
        return postSubmit(data);
      }
    },
    // onError: (value: Record<string, string>) => {
    //   console.info('Event:OnError:', value);
    // },
    onChange: (data: any) => {
      if (data.name && data.name === 'payment_method' && ['google_pay', 'apple_pay'].includes(data.value)) {
        eventbus.emit(Events.FormDisable);
      } else {
        eventbus.emit(Events.FormEnable);
      }
    },
    onLoadStart: () => {
      (async () => {
        setProgress(true);
      })();
    },
    onLoadEnd: () => {
      (async () => {
        setProgress(false);
      })();
    },
  }), [
    apiKey,
    lang,
    payment.amount,
    payment.currency,
    payment.email,
    payment.firstName,
    payment.lastName,
    postSubmit,
    postSubmitForOxxo,
    postSubmitForAffirm,
    postSubmitForExpressCheckout,
    propertyId,
    setProgress,
  ]);

  return (
    <>
      <Html.Form onSubmit={onSubmit} onReset={onReset} ref={formRef as MutableRefObject<HTMLFormElement>}>
        <PaymentElement ref={paymentElementRef} {...defaultProps} />

        <Box w="100%" mt={6} ml={1} data-testid="tos-checkbox" className="tos-checkbox">
          <CheckboxControl
            name="toc"
            onChange={handleChange('toc')}
            isInvalid={!!errors.toc}
            errorMessage={errors.toc}
          >
            {labels.agree} <Link fontWeight="600" onClick={onModalOpen}>{labels.toc}</Link>.
          </CheckboxControl>
        </Box>
      </Html.Form>

      <TocModal
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  );
}
